import type { FunctionComponent } from "react";
import type React from "react";
import { useEffect, useState } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { SubTitleAlignment } from "@similarweb/ui-components";
import { UseCaseHomepage, PlainTooltip } from "@similarweb/ui-components";

import type { ICategory } from "app/common/services/categoryService.types";
import { AutocompleteWebCategories } from "pro-features/components/AutocompleteWebCategories/AutocompleteWebCategories";
import type { IMeteringRawData } from "app/components/metering/types";
import WithTranslation from "pro-features/components/WithTranslation/src/WithTranslation";
import { EContext } from "app/pages/industry-analysis/home/types";
import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { SecondaryHomePageHeaderImageUrl } from "app/UtilitiesAndConstants/Constants/Images";

import { fetchIndustryAnalysisMeteringData } from "./utils/metering/IndustryAnalysisMetering";
import { getBannerTitle } from "./utils/metering/LimitationDisclaimer";

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Title = styled.span`
    ${setFontVariant("body1", colorsPalettes.carbon[400])}
`;

const IconsWrapper = styled(CenteredFlexRow)``;

interface IIndustryAnalysisStartPageProps {
    title: string;
    subtitle: string;
    bodyTextComponent: React.ReactElement;
    subtitlePosition: string;
    listButtons: React.ReactElement[];
    listItems: React.ReactElement[];
    handleItemClick: (item: ICategory, context: EContext, query?: string) => VoidFunction;
    autocompletePlaceholder: string | React.ReactElement;
}

export const IndustryAnalysisStartPage: FunctionComponent<IIndustryAnalysisStartPageProps> = ({
    title,
    subtitle,
    bodyTextComponent,
    subtitlePosition,
    listButtons,
    listItems,
    handleItemClick,
    autocompletePlaceholder,
}) => {
    const [bannerTitle, setBannerTitle] = useState<string>();

    useEffect(() => {
        fetchIndustryAnalysisMeteringData().then((rawData: IMeteringRawData) => {
            const title = getBannerTitle(rawData);
            if (title) {
                setBannerTitle(title);
            }
        });
    }, [listItems]);

    return (
        <WithTranslation>
            {(translate) => (
                <UseCaseHomepage
                    title={translate(title)}
                    titlePosition={subtitlePosition as SubTitleAlignment}
                    subtitle={translate(subtitle)}
                    bodyText={bodyTextComponent}
                    headerImageUrl={SecondaryHomePageHeaderImageUrl}
                    searchComponents={
                        <AutocompleteWebCategories
                            onClick={(item, query) => handleItemClick(item, EContext.AUTOCOMPLETE, query)()}
                            autocompleteProps={{
                                placeholder: autocompletePlaceholder,
                            }}
                        />
                    }
                    listButtons={listButtons}
                    listItems={listItems}
                    banner={
                        bannerTitle
                            ? {
                                  title: (
                                      <TitleContainer>
                                          <Title>{bannerTitle}</Title>
                                          <PlainTooltip
                                              tooltipContent={translate(
                                                  "industry.analysis.metering.limitation.disclaimer.tooltip",
                                              )}
                                          >
                                              <IconsWrapper>
                                                  <SWReactIcons
                                                      size="xs"
                                                      iconName="info-full"
                                                      iconColor={colorsPalettes.carbon["200"]}
                                                  />
                                              </IconsWrapper>
                                          </PlainTooltip>
                                      </TitleContainer>
                                  ),
                                  CustomIcon: (
                                      <SWReactIcons
                                          iconName="spend"
                                          size="sm"
                                      />
                                  ),
                              }
                            : undefined
                    }
                />
            )}
        </WithTranslation>
    );
};
